import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CategoriesPopup from './widgets/CategoriesPopup';
import CardDisplay from './widgets/CardDisplay';
import CardControls from './widgets/CardControls';
import CardTable from './widgets/CardTable';
import CardStatusBadge from './widgets/CardStatusBadge';
import EditSpendCategoriesModal from './widgets/EditSpendCategories';
import EditSpendLimitModal from './widgets/EditSpendLimitModal';
import { ClipLoader } from 'react-spinners';
import { formatNumberCommas } from '../../../../../../utils/CurrencyUtils';
import AddNewCardModal from '../widgets/modals/AddNewCardModal';
import useApi from '../../../../../../utils/AxiosWrapper';
import { unwrapApiResponse } from '../../../../../../utils/unwrapApiResponse';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../../../../../utils/UserContext';
import {
  CustomerWithDriverOnboarding,
  DebitCardWithTruck,
  Entity,
} from '../../../../../../types/apiTypes';
import { useSwipeable } from 'react-swipeable';
import { toast } from 'react-hot-toast';
import { HttpStatusCode } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { InternalBankAccount } from '../../../../../../types/dbTypes';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { DotsThreeVertical, Plus, Warning } from '@phosphor-icons/react';
import CardActionsMenu from './widgets/CardsActionMenu';
import ActivateNewCardModal from '../widgets/modals/ActivateNewCardModal';
import { AccentButtonOutline } from '../../../../../../shared/Buttons';
import { Modal } from 'antd';
import {
  isPreActiveCustomer,
  getDemoCards,
  getDemoAccountInfo,
} from '../../home/utils/demoData';
import { DemoDataAlert } from '../../home/components/DemoDataAlert';
import { DemoDataWrapper } from '../../home/components/DemoDataBadge';

function Cards({ isDriver = false }: { isDriver?: boolean }) {
  const { screenView } = useScreenView();
  const [loadingFreeze, setLoadingFreeze] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [loadingCardImage, setLoadingCardImage] = useState(false);
  const [currCardIdx, setCurrCardIdx] = useState(0);
  const [currCardImage, setCurrCardImage] = useState<string | null>(null);
  const { callProtectedApi } = useApi();
  const { userId, userRecord } = useUser();
  const [popupVisible, setPopupVisible] = useState(false);
  const [newCardPopupVisible, setNewCardPopupVisible] = useState(false);
  const [activateCardPopupVisible, setActivateCardPopupVisible] =
    useState(false);
  const [hideCardDetails, setHideCardDetails] = useState(true);
  const popupRef = useRef<HTMLDivElement>(null);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(
    null
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [nextCardIdx, setNextCardIdx] = useState<number | null>(null);
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const popupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [showEditSpendLimitModal, setShowEditSpendLimitModal] = useState(false);
  const [showEditSpendCategoriesModal, setShowEditSpendCategoriesModal] =
    useState(false);
  const [selectedCardForEdit, setSelectedCardForEdit] =
    useState<DebitCardWithTruck | null>(null);
  const queryClient = useQueryClient();
  const [categoriesPopupOpen, setCategoriesPopupOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoriesAnchorEl, setCategoriesAnchorEl] =
    useState<HTMLElement | null>(null);
  const selectedCardIdRef = useRef<string | null>(null);

  // Check if customer is pre-active (should use demo data)
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  // Only fetch real data if customer is active
  const shouldFetchRealData = !!userId && !isPreActive;

  // Fetch cards data
  const { data: cards = [], isLoading: loadingCards } = useQuery<
    DebitCardWithTruck[],
    Error
  >({
    queryKey: ['debitCards', userId],
    queryFn: async () => {
      const response = await callProtectedApi(
        isDriver
          ? `/debitCards/list/driver/${userId}`
          : `/debitCards/list/${userId}?show_cancelled=true`,
        'get',
        {},
        'Error occurred while fetching your cards.'
      );
      const data = response?.data?.data?.debit_cards;
      if (data && data?.length > 0 && response?.status === HttpStatusCode.Ok) {
        return data;
      }
      return [];
    },
    enabled: shouldFetchRealData,
    staleTime: 60000, // 1 minute
  });

  // Fetch account info
  const fetchAccountInfo = async () => {
    const response = await callProtectedApi(
      `/internalBankAccounts/${userId ?? ''}`,
      'get',
      {},
      'Error occurred while fetching your account info.'
    );
    return unwrapApiResponse<{ internalBankAccount: InternalBankAccount }>(
      response,
      'Error occurred while fetching your account info.'
    ).internalBankAccount;
  };

  const { data: accountInfo, isLoading: loadingAccountInfo } =
    useQuery<InternalBankAccount>({
      queryKey: ['accountInfo', userId],
      queryFn: () => fetchAccountInfo(),
      enabled: shouldFetchRealData && !isDriver,
      staleTime: 15 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });

  // ============================================================================
  // Data Resolution: Use demo data for pre-active customers, real data otherwise
  // ============================================================================

  const displayCards = useMemo(() => {
    return isPreActive ? (getDemoCards() as DebitCardWithTruck[]) : cards;
  }, [isPreActive, cards]);

  const displayAccountInfo = useMemo(() => {
    return isPreActive
      ? getDemoAccountInfo()
      : (accountInfo ?? ({} as InternalBankAccount));
  }, [isPreActive, accountInfo]);

  // Loading states: Never show loading for demo data
  const displayLoadingCards = isPreActive ? false : loadingCards;
  const displayLoadingAccountInfo = isPreActive ? false : loadingAccountInfo;

  const getCardStatus = (c: DebitCardWithTruck) => {
    const cancelled = (c as any)?.is_cancelled === true;
    const frozen = (c as any)?.frozen === true;

    if (cancelled) return 'cancelled';
    if (frozen) return 'frozen';
    return 'active';
  };

  const statusRank: Record<'active' | 'frozen' | 'cancelled', number> = useMemo(
    () => ({
      active: 0,
      frozen: 1,
      cancelled: 2,
    }),
    []
  );

  const sortedCards = useMemo(() => {
    if (!displayCards || displayCards.length === 0) return [];
    return [...displayCards].sort((a, b) => {
      const ra = statusRank[getCardStatus(a) as keyof typeof statusRank] ?? 99;
      const rb = statusRank[getCardStatus(b) as keyof typeof statusRank] ?? 99;
      if (ra !== rb) return ra - rb;

      // Business cards (no truck) first, then stable sort by truck name
      const aHasTruck = a?.truck ? 1 : 0;
      const bHasTruck = b?.truck ? 1 : 0;
      if (aHasTruck !== bHasTruck) return aHasTruck - bHasTruck;
      const an = a?.truck?.truck_display_name ?? '';
      const bn = b?.truck?.truck_display_name ?? '';
      return an.localeCompare(bn);
    });
  }, [displayCards, statusRank]);

  useEffect(() => {
    if (!sortedCards.length) {
      // No cards at all
      selectedCardIdRef.current = null;
      setCurrCardIdx(0);
      return;
    }

    const currentId = selectedCardIdRef.current;

    // First load or nothing selected yet → pick the first card
    if (!currentId) {
      selectedCardIdRef.current = sortedCards[0]?.debit_card_id ?? null;
      setCurrCardIdx(0);
      return;
    }

    // Try to find the same card in the new sorted list
    const idx = sortedCards.findIndex((c) => c.debit_card_id === currentId);

    if (idx === -1) {
      // Card disappeared → fallback to first
      selectedCardIdRef.current = sortedCards[0]?.debit_card_id ?? null;
      setCurrCardIdx(0);
    } else {
      // Card still exists, maybe moved due to status change
      setCurrCardIdx(idx);
    }
  }, [sortedCards]);

  // Derive currCard from sortedCards and currCardIdx
  const currCard = useMemo(() => {
    if (sortedCards.length === 0) return null;
    const validIdx = Math.min(currCardIdx, sortedCards.length - 1);
    return sortedCards[validIdx] || null;
  }, [sortedCards, currCardIdx]);

  // Card actions
  const fetchCardImage = async (debit_card_id: string) => {
    // Prevent fetching for demo data
    if (isPreActive) {
      toast.error('Complete onboarding to access full card details');
      return;
    }

    setLoadingCardImage(true);
    try {
      const response = await callProtectedApi(
        `/debitCards/${debit_card_id}/image`,
        'get',
        {},
        'Error occurred while fetching your card details.'
      );
      const image = response?.data?.data?.card_image_link;
      if (
        image &&
        image?.length > 0 &&
        response?.status === HttpStatusCode.Ok
      ) {
        setCurrCardImage(image);
      }
    } catch (error: any) {
      console.error('Error occurred while fetching your card image.');
    } finally {
      setLoadingCardImage(false);
    }
  };

  const freezeCard = async (debit_card_id: string, frozen: boolean) => {
    // Prevent action for demo data
    if (isPreActive) {
      toast.error('Complete onboarding to manage your cards');
      return;
    }

    setLoadingFreeze(true);
    try {
      const response = await callProtectedApi(
        `/debitCards/freeze`,
        'post',
        {
          is_frozen: frozen,
          debit_card_id: debit_card_id,
        },
        `Error occurred while ${frozen ? 'freezing' : 'unfreezing'} your card.`
      );
      if (response?.status === HttpStatusCode.Ok) {
        toast.success(`Card successfully ${frozen ? 'frozen' : 'unfrozen'}.`);
        setCurrCardImage(null);
        await queryClient.invalidateQueries({
          queryKey: ['debitCards', userId],
        });
      }
    } catch (error: any) {
      console.error(
        `Error occurred while ${frozen ? 'freezing' : 'unfreezing'} your card.`
      );
    } finally {
      setLoadingFreeze(false);
    }
  };

  const showCancelConfirm = (
    debit_card_id: string,
    last_four_digits: string
  ) => {
    Modal.confirm({
      title: `Are you sure you want to cancel your card ending in **** ${last_four_digits}?`,
      content: `Once cancelled, your card will stop working immediately. This action is permanent and cannot be undone.`,
      okText: 'Yes, Cancel Card',
      okType: 'danger',
      cancelText: 'No, Go Back',
      async onOk() {
        await cancelCard(debit_card_id);
      },
    });
  };

  const cancelCard = async (debit_card_id: string) => {
    // Prevent action for demo data
    if (isPreActive) {
      toast.error('Complete onboarding to manage your cards');
      return;
    }

    setLoadingCancel(true);
    try {
      const response = await callProtectedApi(
        `/debitCards/cancel`,
        'post',
        {
          debit_card_id: debit_card_id,
        },
        `Error occurred while cancelling your card.`
      );
      if (response?.status === HttpStatusCode.Ok) {
        toast.success(`Card successfully cancelled.`);
        setCurrCardImage(null);
        await queryClient.invalidateQueries({
          queryKey: ['debitCards', userId],
        });
      }
    } catch (error: any) {
      console.error(`Error occurred while cancelling your card.`);
    } finally {
      setLoadingCancel(false);
    }
  };

  const handleImageError = async () => {
    if (currCard?.debit_card_id) {
      await fetchCardImage(currCard.debit_card_id);
    }
  };

  const togglePopup = (debit_card_id: string | undefined) => {
    if (!debit_card_id) return;
    setActivePopup(activePopup === debit_card_id ? null : debit_card_id);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (!activePopup) return;

    const clickedPopup = displayCards.find((card: DebitCardWithTruck) => {
      const popupRef = popupRefs.current[card?.debit_card_id ?? ''];
      return popupRef?.contains(event.target as Node);
    });

    if (!clickedPopup) {
      setActivePopup(null);
    }
  };

  const handleEditSpendLimit = (card: DebitCardWithTruck) => {
    if (isPreActive) {
      toast.error('Complete onboarding to edit card settings');
      setActivePopup(null);
      return;
    }
    setSelectedCardForEdit(card);
    setShowEditSpendLimitModal(true);
    setActivePopup(null); // Close the popup menu
  };

  const handleEditSpendCategories = (card: DebitCardWithTruck) => {
    if (isPreActive) {
      toast.error('Complete onboarding to edit card settings');
      setActivePopup(null);
      return;
    }
    setSelectedCardForEdit(card);
    setShowEditSpendCategoriesModal(true);
    setActivePopup(null); // Close the popup menu
  };

  const handleOpenCategoriesPopup = (
    e: React.MouseEvent<HTMLDivElement>,
    categories: string[]
  ) => {
    e.stopPropagation();
    setCategoriesAnchorEl(e.currentTarget);
    setSelectedCategories(categories);
    setCategoriesPopupOpen(true);
  };

  const handleCardItemClick = useCallback(
    (card: DebitCardWithTruck, selectedIdx: number) => {
      // track selection by ID
      selectedCardIdRef.current = card.debit_card_id ?? null;

      // Update currCardIdx immediately so the table highlights right away
      setCurrCardIdx(selectedIdx);

      if (selectedIdx !== currCardIdx && !isTransitioning) {
        setIsTransitioning(true);
        setSlideDirection(selectedIdx > currCardIdx ? 'left' : 'right');
        setHideCardDetails(true);
        setNextCardIdx(selectedIdx);
        setCurrCardImage(null);

        setTimeout(() => {
          setNextCardIdx(null);
          setSlideDirection(null);
          setIsTransitioning(false);
        }, 300);
      }

      setActivePopup(null);
    },
    [currCardIdx, isTransitioning]
  );

  const handleSetCurrCardImage = (image: string | null) => {
    setCurrCardImage(image);
  };

  // Swipe handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (sortedCards && sortedCards.length > 1 && !isTransitioning) {
        setIsTransitioning(true);
        setSlideDirection('left');
        setHideCardDetails(true);

        const next = (currCardIdx + 1) % sortedCards.length;
        const nextCard = sortedCards[next];
        selectedCardIdRef.current = nextCard?.debit_card_id ?? null;

        setNextCardIdx(next);
        setCurrCardImage(null);

        setTimeout(() => {
          setCurrCardIdx(next);
          setNextCardIdx(null);
          setSlideDirection(null);
          setIsTransitioning(false);
        }, 300);
      }
    },
    onSwipedRight: () => {
      if (sortedCards && sortedCards.length > 1 && !isTransitioning) {
        setIsTransitioning(true);
        setSlideDirection('right');
        setHideCardDetails(true);

        const next =
          currCardIdx - 1 < 0 ? sortedCards.length - 1 : currCardIdx - 1;
        const nextCard = sortedCards[next];
        selectedCardIdRef.current = nextCard?.debit_card_id ?? null;

        setNextCardIdx(next);
        setCurrCardImage(null);

        setTimeout(() => {
          setCurrCardIdx(next);
          setNextCardIdx(null);
          setSlideDirection(null);
          setIsTransitioning(false);
        }, 300);
      }
    },
    preventScrollOnSwipe: true,
    trackMouse: false,
  });

  // Effects
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setPopupVisible(false);
      }
    };

    if (popupVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupVisible]);

  const activeCardCountByTruckId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of displayCards ?? []) {
      const cancelled = c.is_cancelled === true;
      if (!cancelled && c.truck_id) {
        counts.set(c.truck_id, (counts.get(c.truck_id) ?? 0) + 1);
      }
    }
    return counts;
  }, [displayCards]);

  // Active business cards (no truck) count toward the per-customer cap
  const activeBusinessCardCount = useMemo(
    () =>
      (displayCards ?? []).filter((c) => !c.is_cancelled && !c.truck_id).length,
    [displayCards]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePopup]);

  console.log('sortedCards', sortedCards);

  // Main render function
  const renderScreenView = (screenView: string) => {
    const isLoading = displayLoadingCards || displayLoadingAccountInfo;

    return (
      <div className="flex h-full flex-col gap-y-4 overflow-auto py-4 sm:w-[95%] md:w-full md:px-4 lg:w-[1148px] lg:max-w-[1148px] lg:px-0">
        {isPreActive && <DemoDataAlert />}
        {screenView !== 'desktop' && !isDriver && (
          <AccentButtonOutline
            onClick={() => {
              if (isPreActive) {
                toast.error('Complete onboarding to add cards');
                return;
              }
              setNewCardPopupVisible(true);
            }}
            addClass={`py-[10px] rounded-full w-full md:max-w-[200px] self-end`}
          >
            <div className="flex flex-row items-center space-x-2 align-middle">
              <Plus />
              <p>Add New Card</p>
            </div>
          </AccentButtonOutline>
        )}
        {!isLoading ? (
          <DemoDataWrapper showBadge={isPreActive}>
            <div className="flex w-full flex-col items-center gap-x-[16px] rounded-[4px] border-[1px] border-bordergray bg-white align-middle">
              {newCardPopupVisible && (
                <AddNewCardModal
                  onClose={() => setNewCardPopupVisible(false)}
                  isOpen={newCardPopupVisible}
                  account_id={displayAccountInfo?.account_id ?? ''}
                  activeCardCountByTruckId={activeCardCountByTruckId}
                  activeBusinessCardCount={activeBusinessCardCount}
                />
              )}
              {activateCardPopupVisible && (
                <ActivateNewCardModal
                  onClose={() => setActivateCardPopupVisible(false)}
                  isOpen={activateCardPopupVisible}
                  debit_card_id={currCard?.debit_card_id ?? ''}
                />
              )}

              <div className="relative flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
                <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                  Your TACH Cards
                </h2>
                {screenView === 'desktop' && !isDriver && (
                  <AccentButtonOutline
                    onClick={() => {
                      if (isPreActive) {
                        toast.error('Complete onboarding to add cards');
                        return;
                      }
                      setNewCardPopupVisible(true);
                    }}
                    addClass={`py-[4px] rounded-full`}
                  >
                    <div className="flex flex-row items-center space-x-2 align-middle">
                      <Plus />
                      <p>Add New Card</p>
                    </div>
                  </AccentButtonOutline>
                )}
                {screenView !== 'desktop' && !isPreActive && !isDriver && (
                  <>
                    <DotsThreeVertical
                      onClick={() => setPopupVisible(true)}
                      size={30}
                      className="hover:cursor-pointer hover:opacity-60"
                      weight="bold"
                    />
                    {popupVisible && (
                      <CardActionsMenu
                        popupRef={popupRef as React.RefObject<HTMLDivElement>}
                        currCard={currCard}
                        loadingFreeze={loadingFreeze}
                        loadingCancel={loadingCancel}
                        setPopupVisible={setPopupVisible}
                        setNewCardPopupVisible={setNewCardPopupVisible}
                        setActivateCardPopupVisible={
                          setActivateCardPopupVisible
                        }
                        freezeCard={freezeCard}
                        cancelCard={showCancelConfirm}
                      />
                    )}
                  </>
                )}
              </div>
              {(userRecord?.userType === Entity.Customer || isDriver) && (
                <div
                  className={`relative flex w-full flex-col-reverse items-center justify-center gap-y-4 overflow-hidden px-[40px] py-4 lg:flex-row lg:justify-between lg:space-y-4 lg:pb-4 lg:pt-0`}
                >
                  {/* Card Info Section */}
                  <div className="flex w-full flex-col items-center justify-center lg:max-w-[30%]">
                    <div className="flex w-full max-w-[250px] flex-col items-center justify-center gap-4 md:max-w-[300px] lg:w-[75%] lg:max-w-full lg:items-start lg:justify-start">
                      <div className="flex w-full flex-row items-center justify-start gap-x-10 md:gap-x-20 lg:gap-x-1">
                        <p className="w-[70%] max-w-[85px] text-[12px] font-[400] text-graysecondary">
                          {currCard?.truck ? 'Truck Name' : 'Card Type'}
                        </p>
                        <p className="w-[70%] max-w-[200px] text-[14px] text-primary">
                          {currCard?.truck?.truck_display_name ?? 'Business'}
                        </p>
                      </div>
                      <div className="flex w-full flex-row items-center justify-start gap-x-10 md:gap-x-20 lg:gap-x-1">
                        <p className="w-[70%] max-w-[85px] text-[12px] font-[400] text-graysecondary">
                          Address
                        </p>
                        <p className="w-[70%] max-w-[200px] text-[14px] text-primary">
                          {currCard?.address?.address_st_number}{' '}
                          {currCard?.address?.address_st_name},
                          {currCard?.address?.address_unit &&
                          currCard?.address?.address_unit?.length ? (
                            <> {currCard.address.address_unit},</>
                          ) : null}{' '}
                          {currCard?.address?.address_city},{' '}
                          {currCard?.address?.address_state}{' '}
                          {currCard?.address?.address_zip}
                        </p>
                      </div>
                      <div className="flex w-full flex-row items-center justify-start gap-x-10 md:gap-x-20 lg:gap-x-1">
                        <p className="w-[70%] max-w-[85px] text-[12px] font-[400] text-graysecondary">
                          Status
                        </p>
                        {!currCard?.is_cancelled &&
                        currCard?.physical_card_activated_timestamp === null ? (
                          <div className="relative">
                            <CardStatusBadge card={currCard} />
                            <div className="absolute -right-7 top-[50%] -translate-y-1/2">
                              <Warning size={24} className="text-[#E2660C]" />
                            </div>
                          </div>
                        ) : (
                          <CardStatusBadge card={currCard} />
                        )}
                      </div>
                      <div className="flex w-full flex-row items-center justify-start gap-x-10 md:gap-x-20 lg:gap-x-1">
                        <p className="w-[70%] max-w-[85px] text-[12px] font-[400] text-graysecondary">
                          Daily Spend
                        </p>
                        <p className="w-[70%] max-w-[85px] text-[14px] text-primary">
                          $
                          {formatNumberCommas(currCard?.daily_spend_limit ?? 0)}
                        </p>
                      </div>
                      <div className="flex w-full flex-row items-center justify-start gap-x-10 md:gap-x-20 lg:gap-x-1">
                        <p className="w-[70%] max-w-[85px] text-[12px] font-[400] text-graysecondary">
                          Monthly Spend
                        </p>
                        <p className="w-[70%] max-w-[85px] text-[14px] text-primary">
                          $
                          {formatNumberCommas(
                            currCard?.thirty_day_spend_limit ?? 0
                          )}
                        </p>
                      </div>
                      {!currCard?.is_cancelled &&
                        currCard?.physical_card_activated_timestamp === null &&
                        screenView !== 'desktop' && (
                          <div className="flex flex-row items-center justify-center rounded-[8px] border-[1px] border-[#E2660C] bg-[#FCF0E7] px-2 py-1 align-middle backdrop-blur-[24%]">
                            <Warning
                              size={40}
                              className="mr-1.5 text-[#E2660C]"
                            />
                            <p className="text-[12px] text-primary">
                              Your physical card is not yet activated. Please
                              activate it once you’ve received it.
                            </p>
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Card Display Section */}
                  <CardDisplay
                    cards={sortedCards}
                    currCard={currCard}
                    currCardIdx={currCardIdx}
                    currCardImage={currCardImage}
                    hideCardDetails={hideCardDetails}
                    userRecord={userRecord}
                    handleImageError={handleImageError}
                    fetchCardImage={fetchCardImage}
                    setHideCardDetails={setHideCardDetails}
                    setCurrCardIdx={setCurrCardIdx}
                    swipeHandlers={swipeHandlers}
                    setCurrCardImage={handleSetCurrCardImage}
                    cardImageLoading={loadingCardImage}
                    slideDirection={slideDirection}
                    isTransitioning={isTransitioning}
                    nextCardIdx={nextCardIdx}
                    setSlideDirection={setSlideDirection}
                    setIsTransitioning={setIsTransitioning}
                    setNextCardIdx={setNextCardIdx}
                    onArrowNavigate={(nextIdx) => {
                      selectedCardIdRef.current =
                        sortedCards[nextIdx]?.debit_card_id ?? null;
                    }}
                  />

                  {/* Card Controls Section */}
                  {screenView === 'desktop' && !isDriver ? (
                    <div className="flex w-[30%] flex-shrink-0 items-start justify-center">
                      <CardControls
                        setPopupVisible={setPopupVisible}
                        setNewCardPopupVisible={setNewCardPopupVisible}
                        setActivateCardPopupVisible={
                          setActivateCardPopupVisible
                        }
                        currCard={currCard}
                        loadingFreeze={loadingFreeze}
                        loadingCancel={loadingCancel}
                        freezeCard={freezeCard}
                        cancelCard={showCancelConfirm}
                        isPreActive={isPreActive}
                      />
                    </div>
                  ) : (
                    <div className="flex w-[30%]" />
                  )}
                </div>
              )}
            </div>
          </DemoDataWrapper>
        ) : (
          <div className="flex h-[326px] w-full items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-white">
            <ClipLoader
              color="#1D213B"
              loading={isLoading}
              size={60}
              aria-label="Loading Spinner"
              data-testid="loader"
              className="flex h-full w-full items-center justify-center"
            />
          </div>
        )}

        {/* Card Table Section */}
        <CardTable
          cards={sortedCards}
          loadingCards={displayLoadingCards}
          loadingAccountInfo={displayLoadingAccountInfo}
          currCard={currCard}
          currCardIdx={currCardIdx}
          isDriver={isDriver}
          screenView={screenView}
          activePopup={activePopup}
          popupRefs={popupRefs}
          togglePopup={togglePopup}
          freezeCard={freezeCard}
          isPreActive={isPreActive}
          cancelCard={showCancelConfirm}
          handleEditSpendLimit={handleEditSpendLimit}
          handleEditSpendCategories={handleEditSpendCategories}
          setActivateCardPopupVisible={() => {
            if (isPreActive) {
              toast.error('Complete onboarding to activate cards');
              return;
            }
            setActivateCardPopupVisible(true);
            setPopupVisible(true);
          }}
          handleOpenCategoriesPopup={handleOpenCategoriesPopup}
          handleCardItemClick={handleCardItemClick}
        />
      </div>
    );
  };

  return (
    <>
      {renderScreenView(screenView)}
      {showEditSpendLimitModal && !isPreActive && (
        <EditSpendLimitModal
          isOpen={showEditSpendLimitModal}
          onClose={() => setShowEditSpendLimitModal(false)}
          cards={displayCards}
          loadingCards={displayLoadingCards}
          selectedCard={selectedCardForEdit || undefined}
        />
      )}
      {showEditSpendCategoriesModal && !isPreActive && (
        <EditSpendCategoriesModal
          isOpen={showEditSpendCategoriesModal}
          onClose={() => setShowEditSpendCategoriesModal(false)}
          loadingCards={displayLoadingCards}
          selectedCard={selectedCardForEdit || undefined}
        />
      )}
      <CategoriesPopup
        categories={selectedCategories}
        anchorEl={categoriesAnchorEl}
        isOpen={categoriesPopupOpen}
        onClose={() => setCategoriesPopupOpen(false)}
      />
    </>
  );
}

export default Cards;
